import React, { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChatHeading } from '../ChatHeading';
import { ChatInput } from './ChatInput';
import { UsageValueBanner } from './UsageValueBanner';
import { useChatStore } from '../../store/chatStore';
import {
  QuickOnboarding,
  useQuickOnboardingConfig,
} from '../../../common/QuickOnboarding';
import { QuickActionButtons } from '../CenteredInput/QuickActionButtons';
import { OptionsBox } from '../CenteredInput/OptionsBox';
import { LibraryItem } from '../Library/libraryData';
import { useDashboards, Dashboard } from '../../../../api/dashboard';
import { useLayoutStore } from '../../../../store/layoutStore';

// import SpotlightCard from '../SpotlightCard';
// import { useSpotlightStore } from '../../store/spotlightStore';

interface PleriChatLandingProps {
  isCompact?: boolean;
}

export const PleriChatLanding: React.FC<PleriChatLandingProps> = ({
  isCompact,
}) => {
  const navigate = useNavigate();
  const { isMiniMode } = useLayoutStore();
  const isFocusedView = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.focusedViewOpen || false;
  });

  const [activeAction, setActiveAction] = useState<{
    name: string;
    icon: React.ReactNode;
    items: LibraryItem[];
  } | null>(null);

  const quickOnboardingConfig = useQuickOnboardingConfig();
  const hasQuickOnboarding =
    !quickOnboardingConfig.isLoading &&
    quickOnboardingConfig.visibleTools.length > 0;

  // Fetch dashboards for navigation lookup
  const dashboardsQuery = useDashboards({
    limit: 50,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const handleDashboardNavigate = (item: LibraryItem) => {
    const { dashboards = [] } = dashboardsQuery.data || {};

    // First try to find by dashboardId if provided
    let targetDashboard: Dashboard | undefined = item.dashboardId
      ? dashboards.find((d: Dashboard) => d.dashboardId === item.dashboardId)
      : undefined;

    // If not found and dashboardTitleMatch is provided, find by title match
    if (!targetDashboard && item.dashboardTitleMatch) {
      targetDashboard = dashboards.find((d: Dashboard) =>
        d.title.toLowerCase().includes(item.dashboardTitleMatch!.toLowerCase()),
      );
    }

    if (targetDashboard) {
      navigate({
        to: '/dashboard/$dashboardId',
        params: { dashboardId: targetDashboard.dashboardId },
        search: {
          tenantId: targetDashboard.tenantId,
          ownerId: targetDashboard.ownerId,
          scope: targetDashboard.scope,
          fromQuickAction: 'true', // Flag to indicate navigation from quick action
        },
      });
    } else {
      // Fallback: navigate to dashboard list
      navigate({
        to: '/dashboard',
      });
    }
    setActiveAction(null);
  };

  const handleCloseOptionsBox = useCallback(() => {
    setActiveAction(null);
  }, []);

  // const featureSpotlight = useSpotlightStore((s) => s.getFeatureSpotlight());
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 flex-col items-center">
        <div className="w-full max-w-3xl">
          <div className="flex flex-col items-center pt-32">
            {/* {featureSpotlight && (
              <div className="mb-4 w-full">
                <SpotlightCard
                  message={featureSpotlight.message}
                  onClick={() =>
                    PleriClient.updateChatInput({
                      prompt: featureSpotlight.prompt,
                      autoSubmit: true,
                    })
                  }
                  buttonText="Review"
                />
              </div>
            )} */}
            <ChatHeading
              className={
                isCompact || isFocusedView
                  ? 'mx-4 !mb-4 text-2xl leading-tight'
                  : '!mb-4 text-4xl leading-tight'
              }
            />
            <div id="chameleon-banner-slot-pleri-chat-landing"></div>
            <UsageValueBanner />
            <ChatInput isCompact={isCompact} />

            {/* Quick Action Buttons */}
            {!isFocusedView &&
              !hasQuickOnboarding &&
              !quickOnboardingConfig.isLoading && (
                <div className="relative w-full">
                  <div
                    className={`transition-opacity duration-200 ease-in-out ${
                      activeAction ? 'invisible' : 'visible'
                    }`}
                  >
                    <QuickActionButtons
                      isSmallWindow={isCompact || false}
                      onActionClick={(name, icon, items) => {
                        setActiveAction({ name, icon, items });
                      }}
                    />
                  </div>
                  {activeAction && (
                    <div className="absolute inset-x-0 top-0 z-10">
                      <OptionsBox
                        title={activeAction.name}
                        icon={activeAction.icon}
                        items={activeAction.items}
                        onOptionSelect={(item) => {
                          const store = useChatStore.getState();
                          store.setInputPrompt(item.prompt);
                          store.setPendingMetrics({
                            eventLocation: 'quickAction',
                            eventName: 'quickActionItemClick',
                            eventType: 'autoSubmitQuery',
                            eventValue: item.id,
                            eventDetails: {
                              category: item.category,
                              title: item.title,
                              tags: item.tags,
                            },
                          });
                          store.submitInputPrompt();
                          setActiveAction(null);
                        }}
                        onDashboardNavigate={handleDashboardNavigate}
                        onClose={handleCloseOptionsBox}
                      />
                    </div>
                  )}
                </div>
              )}

            {/* Quick Onboarding Tools */}
            {!isFocusedView && (
              <div className="w-full px-4 pt-8">
                <QuickOnboarding isCompact={isCompact} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DAP Anchor: For embedding DAP banner - exact same structure as ChatInput */}
      {!isMiniMode && (
        <div className="w-full px-4 pb-8">
          <div className="mx-auto w-full max-w-3xl">
            <div
              id="dap-pleri-welcome-anchor"
              data-dap-anchor="pleri-welcome-banner"
              className="h-4 w-full"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
    </div>
  );
};
